/**
 * Cashbox Close Page
 *
 * Mobile-first page for closing the cash register with final cash count.
 * Shows expected cash reference, payment method breakdown, INGRESOS section,
 * movements list, bills list, and calculates difference between expected and actual amounts.
 * Optimized for dark mode and responsive layouts.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Lock,
  Calculator,
  Banknote,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  ShoppingCart,
  Receipt,
  FileText,
  RefreshCcw,
  Wallet,
  CreditCard,
} from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDateTime } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/toaster'
import type { PaymentSummary } from './cashbox-constants'
import { PaymentMethodBreakdown } from './PaymentMethodBreakdown'
import { useIsMobile } from '@/hooks/useIsMobile'

interface CashboxMovement {
  id: number
  movement_type: string
  amount: number
  description: string | null
  reference: string | null
  created_at: string | null
  user_id: number
}

interface CashboxDetail {
  id: number
  is_open: boolean
  opening_time: string | null
  closing_time: string | null
  initial_amount: number
  current_amount: number
  expected_amount: number
  difference: number
  total_sales: number
  total_income: number
  total_expense: number
  movements: CashboxMovement[]
  subtotal_credit: number
  total_credit_sale_tax: number
  total_credit_discount: number
  total_credit: number
  subtotal_cash: number
  total_cash_sale_tax: number
  total_cash_discount: number
  total_cash: number
  subtotal_income: number
  total_income_sale_tax: number
  total_income_discount: number
  total_income_amount: number
  total_deposited: number
  total_devolutions: number
  opened_by_name: string | null
  closed_by_name: string | null
}

interface CashboxBill {
  id: number
  bill_number: string | null
  type: string
  status: string
  client_name: string | null
  subtotal: number
  total_sale_tax: number
  total: number
  pay_type: string | null
  created_at: string | null
}

interface CashboxBillsData {
  items: CashboxBill[]
  total: number
  total_cash: number
  total_credit: number
  total_amount: number
}

const billTypeLabels: Record<string, string> = {
  ticket: t('cashbox.billTypeTicket'),
  invoice: t('cashbox.billTypeInvoice'),
  proforma: t('cashbox.billTypeProforma'),
  credit_note: t('cashbox.billTypeCreditNote'),
}

const billStatusLabels: Record<string, { label: string; color: string }> = {
  pending: { label: t('cashbox.billStatusPending'), color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  paid: { label: t('cashbox.billStatusPaid'), color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  finalized: { label: t('cashbox.billStatusFinalized'), color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  cancelled: { label: t('cashbox.billStatusCancelled'), color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
}

const movementTypeLabels: Record<string, { label: string; color: string; icon: typeof TrendingUp }> = {
  sale: { label: t('cashbox.movementSale'), color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: ShoppingCart },
  income: { label: t('cashbox.movementIncome'), color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: TrendingUp },
  expense: { label: t('cashbox.movementExpense'), color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: TrendingDown },
  withdrawal: { label: t('cashbox.movementWithdrawal'), color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: TrendingDown },
  deposit: { label: t('cashbox.movementDeposit'), color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Receipt },
  devolution: { label: t('cashbox.movementDevolution'), color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: RefreshCcw },
}

export function CashboxClosePage() {
  const navigate = useNavigate()
  // En el celular el autofoco abre el teclado y tapa la mitad de la pantalla
  const isMobile = useIsMobile()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [finalAmount, setFinalAmount] = useState('')
  const [notes, setNotes] = useState('')

  // Fetch current cashbox
  const { data: cashbox, isLoading } = useQuery({
    queryKey: ['my-cashbox'],
    queryFn: async () => {
      const response = await api.get('/cashboxes/my-cashbox')
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Fetch payment summary breakdown
  const { data: paymentSummary } = useQuery<PaymentSummary>({
    queryKey: ['cashbox-payment-summary', cashbox?.id],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/${cashbox.id}/payment-summary`)
      return response.data
    },
    enabled: !!cashbox?.id,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Fetch cashbox detail (for INGRESOS breakdown and movements)
  const { data: cashboxDetail } = useQuery<CashboxDetail>({
    queryKey: ['cashbox-detail', cashbox?.id],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/${cashbox.id}`)
      return response.data as CashboxDetail
    },
    enabled: !!cashbox?.id,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Fetch bills for this cashbox
  const { data: billsData, isLoading: billsLoading } = useQuery<CashboxBillsData>({
    queryKey: ['cashbox-bills', cashbox?.id],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/${cashbox.id}/bills`)
      return response.data as CashboxBillsData
    },
    enabled: !!cashbox?.id,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Close cashbox mutation
  const closeMutation = useMutation({
    mutationFn: async (data: { finalAmount: number; notes: string }) => {
      if (!cashbox?.id) {
        throw new Error('No cashbox assigned')
      }
      const response = await api.post(`/cashboxes/${cashbox.id}/close`, {
        final_amount: data.finalAmount,
        notes: data.notes || t('cashbox.closeNote'),
      })
      return response.data
    },
    onSuccess: async () => {
      // La pantalla de caja muestra el estado y la lista: sin esperar a que se
      // recarguen, aparecía la caja como estaba antes hasta refrescar.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-cashbox'] }),
        queryClient.invalidateQueries({ queryKey: ['cashbox-history'] }),
      ])
      toast({
        title: t('cashbox.closed'),
        description: t('cashbox.closedSuccess'),
        variant: 'success',
      })
      navigate('/cashbox')
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message || t('cashbox.closeError'),
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const amount = parseFloat(finalAmount) || 0
    closeMutation.mutate({ finalAmount: amount, notes })
  }


  if (isLoading || (cashbox?.is_open && !paymentSummary)) {
    return (
      <div className="space-y-4 px-1">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  // If cashbox is not open, redirect
  if (!cashbox?.is_open) {
    navigate('/cashbox')
    return null
  }

  const countedAmount = parseFloat(finalAmount) || 0

  const cashSales = paymentSummary?.drawer_cash_sales || 0
  const cashDeposits = paymentSummary?.drawer_cash_deposits || 0
  const devolutions = paymentSummary?.drawer_cash_devolutions || 0
  const expenses = paymentSummary?.drawer_expenses || 0
  const initialAmount = paymentSummary?.drawer_initial || 0
  const expectedCashInRegister = paymentSummary?.expected_cash || 0

  const difference = countedAmount - expectedCashInRegister
  const hasDifference = Math.abs(difference) > 0.01

  return (
    <div className="space-y-4 pb-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/cashbox')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('cashbox.closeCashbox')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('cashbox.openedAt')}: {formatDateTime(cashbox.opening_time)}
          </p>
        </div>
      </div>

      {/* Efectivo Esperado en Caja */}
      <Card className="border-primary/30 bg-primary/5 dark:bg-primary/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-primary">
            <Wallet className="h-5 w-5" />
            {t('cashbox.expectedCashInRegister')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          <div className="flex justify-between items-center py-1">
            <span className="text-sm text-muted-foreground">{t('cashbox.initialAmount')}</span>
            <span className="font-medium">{formatCurrency(initialAmount)}</span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-sm text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
              {t('cashbox.cashSalesInCash')}
            </span>
            <span className="font-medium text-emerald-600 dark:text-emerald-400">
              +{formatCurrency(cashSales)}
            </span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-sm text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-sky-500" />
              {t('cashbox.depositsInCash')}
            </span>
            <span className="font-medium text-sky-600 dark:text-sky-400">
              +{formatCurrency(cashDeposits)}
            </span>
          </div>
          {devolutions > 0 && (
            <div className="flex justify-between items-center py-1">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <RefreshCcw className="h-3.5 w-3.5 text-yellow-500" />
                {t('cashbox.cashDevolutions')}
              </span>
              <span className="font-medium text-red-600 dark:text-red-400">
                -{formatCurrency(devolutions)}
              </span>
            </div>
          )}
          {expenses > 0 && (
            <div className="flex justify-between items-center py-1">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                {t('cashbox.expenses')}
              </span>
              <span className="font-medium text-red-600 dark:text-red-400">
                -{formatCurrency(expenses)}
              </span>
            </div>
          )}
          <div className="border-t border-primary/20 pt-2 mt-2 flex justify-between items-center">
            <span className="font-semibold text-primary">{t('cashbox.expectedCash')}</span>
            <span className="font-bold text-2xl text-primary">
              {formatCurrency(expectedCashInRegister)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Close Form - Cash Count, Difference & Submit */}
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Warning if difference */}
        {hasDifference && countedAmount > 0 && (
          <Card
            className={
              difference > 0
                ? 'border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/60 dark:bg-emerald-950/30'
                : 'border-orange-200 dark:border-orange-800/40 bg-orange-50/60 dark:bg-orange-950/30'
            }
          >
            <CardContent className="p-3 flex items-start gap-3">
              {difference > 0 ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-orange-500 dark:text-orange-400 shrink-0 mt-0.5" />
              )}
              <div>
                <p
                  className={`text-sm font-medium ${
                    difference > 0
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-orange-700 dark:text-orange-300'
                  }`}
                >
                  {difference > 0 ? t('cashbox.surplus') : t('cashbox.shortage')}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {difference > 0
                    ? t('cashbox.surplusDescription')
                    : t('cashbox.shortageDescription')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cash Count */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Banknote className="h-4 w-4" />
              {t('cashbox.countCash')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="finalAmount">{t('cashbox.finalAmount')} *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  ₡
                </span>
                <Input
                  id="finalAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={finalAmount}
                  onChange={(e) => setFinalAmount(e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  className="pl-8 h-14 text-2xl font-bold"
                  autoFocus={!isMobile}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('cashbox.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  hasDifference
                    ? t('cashbox.explainDifference')
                    : t('cashbox.closeNotesPlaceholder')
                }
                className="resize-none"
                rows={2}
              />
              {hasDifference && !notes && (
                <p className="text-xs text-orange-600 dark:text-orange-400">
                  {t('cashbox.recommendExplainDifference')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-12"
            onClick={() => navigate('/cashbox')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="destructive"
            className="h-12"
            disabled={closeMutation.isPending || !finalAmount}
          >
            {closeMutation.isPending && <Spinner size="sm" className="mr-2" />}
            <Lock className="h-4 w-4 mr-2" />
            {t('cashbox.closeCashbox')}
          </Button>
        </div>
      </form>

      {/* INGRESOS Section — desglose por tipo de venta y método de pago */}
      {paymentSummary && (
        <Card>
          <CardHeader className="pb-2 bg-blue-50 dark:bg-blue-900/20">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <Calculator className="h-5 w-5" />
              {t('cashbox.ingresos')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <PaymentMethodBreakdown
              title={t('cashbox.cashSalesTotal')}
              total={paymentSummary.total_sales}
              breakdown={paymentSummary.sales_by_method}
              variant="cash"
              icon={ShoppingCart}
              signed
            />
            <PaymentMethodBreakdown
              title={t('cashbox.creditDepositsTitle')}
              total={paymentSummary.total_deposits}
              breakdown={paymentSummary.deposits_by_method}
              variant="deposit"
              icon={Wallet}
              signed
            />
            <PaymentMethodBreakdown
              title={t('cashbox.creditSalesEmitted')}
              total={paymentSummary.total_credit_sales}
              variant="credit"
              icon={CreditCard}
              hint={t('cashbox.creditSalesEmittedHint')}
            />
            {paymentSummary.total_devolutions > 0 && (
              <div className="flex justify-between items-center p-2.5 rounded-md border border-yellow-200 dark:border-yellow-800/40 bg-yellow-50/60 dark:bg-yellow-950/30">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <RefreshCcw className="h-3.5 w-3.5 text-yellow-500 dark:text-yellow-400" />
                  {t('cashbox.devolutions')}
                </span>
                <span className="text-right">
                  <span className="block font-semibold text-red-600 dark:text-red-400">
                    -{formatCurrency(paymentSummary.total_devolutions)}
                  </span>
                  {(paymentSummary.noncash_devolutions ?? 0) > 0 && (
                    <span className="block text-xs text-muted-foreground">
                      {t('cashbox.devolutionsSplit', {
                        cash: formatCurrency(paymentSummary.drawer_cash_devolutions),
                        others: formatCurrency(paymentSummary.noncash_devolutions ?? 0),
                      })}
                    </span>
                  )}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Movements */}
      {cashboxDetail && cashboxDetail.movements.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('cashbox.movements')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-2">
              {cashboxDetail.movements.map((movement) => {
                const typeInfo = movementTypeLabels[movement.movement_type] || {
                  label: movement.movement_type,
                  color: 'bg-gray-100 text-gray-700',
                  icon: Receipt,
                }
                const Icon = typeInfo.icon
                const isIncome = ['sale', 'income', 'deposit'].includes(movement.movement_type)

                return (
                  <div
                    key={movement.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                  >
                    <div className={`p-2 rounded-full ${typeInfo.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${typeInfo.color}`}>
                          {typeInfo.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {movement.description || '-'}
                      </p>
                      {movement.created_at && (
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(movement.created_at)}
                        </p>
                      )}
                    </div>
                    <p className={`font-bold shrink-0 ${isIncome ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {isIncome ? '+' : '-'}{formatCurrency(movement.amount)}
                    </p>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bills / Facturas */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('cashbox.bills')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {billsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          ) : !billsData || billsData.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              {t('cashbox.noBills')}
            </p>
          ) : (
            <>
              {/* Bills Summary */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Banknote className="h-4 w-4 text-green-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('cashbox.cashSection')}</p>
                  <p className="font-bold text-green-600">{formatCurrency(billsData.total_cash)}</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CreditCard className="h-4 w-4 text-orange-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('cashbox.creditSection')}</p>
                  <p className="font-bold text-orange-600">{formatCurrency(billsData.total_credit)}</p>
                </div>
                <div className="bg-primary/10 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('cashbox.total')}</p>
                  <p className="font-bold text-primary">{formatCurrency(billsData.total_amount)}</p>
                </div>
              </div>

              {/* Bills List */}
              <div className="space-y-2">
                {billsData.items.map((bill) => {
                  const statusInfo = billStatusLabels[bill.status] || {
                    label: bill.status,
                    color: 'bg-gray-100 text-gray-700',
                  }

                  return (
                    <div
                      key={bill.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/bills/${bill.id}`)}
                    >
                      <div className="p-2 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            #{bill.id}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {billTypeLabels[bill.type] || bill.type}
                          </Badge>
                          <Badge variant="outline" className={`text-xs ${statusInfo.color}`}>
                            {statusInfo.label}
                          </Badge>
                          {bill.pay_type === 'credit' && (
                            <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                              {t('cashbox.creditSection')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {bill.client_name || t('cashbox.generalClient')}
                        </p>
                        {bill.created_at && (
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(bill.created_at)}
                          </p>
                        )}
                      </div>
                      <p className="font-bold text-primary shrink-0">
                        {formatCurrency(bill.total)}
                      </p>
                    </div>
                  )
                })}
              </div>

              <p className="text-center text-sm text-muted-foreground mt-3">
                {billsData.total} factura{billsData.total !== 1 ? 's' : ''} en total
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
